import React, { memo, useEffect, useMemo, useRef } from "react";
import { useEditor } from "../../hooks/useEditor";
import { DesignsPanel, IDesignsPanelItem, IDesignsPanelRef } from "./designs-panel";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../contexts/dashboard-context";
import { useDefinition } from "../../hooks/useDefinition";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useResetDefinitionForDesign } from "../../hooks/useResetDefinitionForDesign";
import { useDesigns } from "./use-designs";

export const DesignsPanelContainer = memo(() => {
	const editor = useEditor();
	const { client } = editor;
	const { data: designs = [] } = useDesigns();
	const designsPanelRef = useRef<IDesignsPanelRef>(null);

	const dashboard = useDashboardContext();
	const { createErrorHandler } = useErrorHandling();

	const definition = useDefinition();

	const [definitionToReuse, setDefinitionToReuse] = useResetDefinitionForDesign();

	useEffect(() => {
		if (designsPanelRef.current) {
			designsPanelRef.current.scrollIntoView(definition?.product.predefinedName);
		}
	}, [definition?.product.predefinedName]);

	useEffect(() => {
		if (designsPanelRef.current)
			designsPanelRef.current.scrollIntoView(definition?.product.predefinedName, false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [designs]);

	const items = useMemo<readonly IDesignsPanelItem[]>(
		() =>
			designs.map((design) => ({
				productCode: design.productCode.toLowerCase(),
				predefinedProductName: design.predefinedProductName,
				absoluteDesignUrl: design.predefinedProductName,
				thumbUrl: design.packshotUrl.getAbsoluteUrl(client),
			})),
		[client, designs],
	);

	const { busyBlocker } = useBusyBlockerContext();

	return (
		<DesignsPanel
			ref={designsPanelRef}
			selectedPredefinedProductName={definition?.product?.predefinedName}
			designs={items}
			onClick={(predefinedProductName) => {
				if (!definition) return;

				if (!definitionToReuse) {
					const copyApiData = editor.client.copyApiData.bind(editor.client);
					setDefinitionToReuse(definition.clone(copyApiData));
				}

				if (definition.product?.predefinedName) {
					designsPanelRef.current?.scrollIntoView(predefinedProductName);
				}

				busyBlocker
					.during(
						dashboard.applyAlternativeDesignAsync(
							eXpress.PredefinedProductName.fromString(predefinedProductName),
							definitionToReuse,
						),
					)
					.catch(createErrorHandler());
			}}
		/>
	);
});
DesignsPanelContainer.displayName = "DesignsPanelContainer";
